import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Modal, LoadingIcon, Button, ButtonType, Text } from '@junglescout/edna'

import { UpsellToggle } from 'ui_elements/UpsellToggle/UpsellToggle'

import { parseCurrency } from 'helpers/currency'

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 75vh;
  max-height: 800px;
  min-width: 775px;
  width: 100%;
  overflow-y: auto;
`

const PlanBlocksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
  width: 100%;
`

const PlanBlockWrapper = styled.div`
  width: 300px;
  border: 1px solid ${props => props.theme.colors.grey100};
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  background: white;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 2px 3px ${props => props.theme.colors.blackOpacity10};
  }
`

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
`

const PlanFeatures = styled.div`
  flex: 1;
  margin-bottom: 20px;
`

const FeatureSection = styled.div`
  margin-bottom: 15px;
`

const FeatureList = styled.ul`
  padding-left: 20px;
  margin: 0;
`

const LoadingWrapper = styled.div`
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`

const StyledButton = styled(Button)`
  height: 30px;
  min-height: 25px;
  width: 50px;
  border-radius: 4px;
`

const parsePlansByFrequency = plans => {
  // If plans is null or undefined, return empty structure
  if (!plans) {
    return {}
  }

  // Handle the expected structure: object with monthly/yearly keys
  const result = { ...plans }

  // Sort yearly plans by price in descending order if available
  if (result.yearly && result.yearly.length > 0) {
    result.yearly.sort((a, b) => b.price - a.price)
  }

  return result
}

const PlanBlock = ({
  name,
  price,
  currency,
  interval,
  seats,
  link,
  line_items
}) => {
  const handleSelectPlan = () => {
    window.open(link, '_blank', 'noopener noreferrer')
  }

  return (
    <PlanBlockWrapper>
      <PlanHeader>
        <Text
          variant="headingLg"
          margin="0 0 16px 0"
          element="h2"
          align="center">
          {name}
        </Text>
        <Text
          variant="headingMd"
          margin="0 0 4px 0"
          element="h3"
          align="center">
          {parseCurrency(price, currency)}
        </Text>
        <Text
          variant="headingXs"
          margin="0 0 8px 0"
          element="h4"
          color="grey600"
          align="center">
          {interval === 'month' ? 'per month' : 'per year'}
        </Text>
        <Text
          variant="headingXs"
          margin="0 0 16px 0"
          color="grey900"
          element="h5"
          align="center">
          {seats} {seats === 1 ? 'Seat' : 'Seats'}
        </Text>
        <StyledButton
          btnType={ButtonType.PRIMARY}
          onClick={handleSelectPlan}
          width="100%">
          Buy Now
        </StyledButton>
      </PlanHeader>

      <PlanFeatures>
        {line_items &&
          line_items.map(section => (
            <FeatureSection key={`section-${section.heading}`}>
              <Text variant="headingSm" margin="0 0 8px 0" element="h4">
                {section.heading}
              </Text>
              <FeatureList>
                {section.items.map(item => (
                  <Text
                    variant="bodySm"
                    margin="0 0 8px 0"
                    color="grey900"
                    element="li"
                    key={`item-${section.heading}-${item}`}>
                    {item}
                  </Text>
                ))}
              </FeatureList>
            </FeatureSection>
          ))}
      </PlanFeatures>
    </PlanBlockWrapper>
  )
}

export const PurchaseModal = ({
  isVisible,
  isLoading,
  handleBackdropClick,
  plans
}) => {
  const [plansByFrequency, setPlansByFrequency] = useState({})
  const [billingFrequency, setBillingFrequency] = useState('yearly')
  const [showToggle, setShowToggle] = useState(false)

  useEffect(() => {
    const parsedPlans = parsePlansByFrequency(plans)
    setPlansByFrequency(parsedPlans)

    // Show toggle only if we have both monthly and yearly plans
    if (parsedPlans.monthly && parsedPlans.yearly) {
      setShowToggle(true)
      setBillingFrequency('yearly')
    } else if (parsedPlans.monthly) {
      setBillingFrequency('monthly')
      setShowToggle(false)
    } else if (parsedPlans.yearly) {
      setBillingFrequency('yearly')
      setShowToggle(false)
    } else {
      setBillingFrequency('all')
      setShowToggle(false)
    }
  }, [plans])

  const toggleBillingFrequency = () => {
    setBillingFrequency(billingFrequency === 'monthly' ? 'yearly' : 'monthly')
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <LoadingWrapper>
          <LoadingIcon color="primary" size="50px" />
        </LoadingWrapper>
      )
    }

    const visiblePlans = plansByFrequency[billingFrequency] || []
    return (
      <ModalContent>
        {showToggle && (
          <ToggleWrapper>
            <UpsellToggle
              billingFrequency={billingFrequency}
              toggleAction={toggleBillingFrequency}
            />
          </ToggleWrapper>
        )}
        <PlanBlocksContainer>
          {visiblePlans.map((planInfo, index) => (
            <PlanBlock key={`${planInfo.name}-${index}`} {...planInfo} />
          ))}
        </PlanBlocksContainer>
      </ModalContent>
    )
  }

  return (
    <Modal
      open={isVisible}
      onOpenChange={open => {
        if (!open) {
          handleBackdropClick()
        }
      }}>
      <Modal.Content minWidth="800px" title="Subscription Purchase">
        {renderBody()}
      </Modal.Content>
    </Modal>
  )
}

PurchaseModal.defaultProps = {
  isVisible: false,
  isLoading: false,
  handleBackdropClick: () => {}
}

PurchaseModal.propTypes = {
  isVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  handleBackdropClick: PropTypes.func
}

PlanBlock.propTypes = {
  name: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  interval: PropTypes.string.isRequired,
  seats: PropTypes.number.isRequired,
  link: PropTypes.string.isRequired,
  line_items: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.string).isRequired
    })
  ).isRequired
}
